import {useMemo, useState} from 'react';
import type {Selection} from '@react-types/shared';
import {useMenuTriggerState} from '../menu';
import {areSelectionsEqual, useControlledState} from '../utils';
import type {Collection} from '../../collections/types';
import type {
  MultipleSelectListState,
  MultipleSelectionProps,
  MenuTriggerState,
  MenuTriggerProps,
  Expandable,
  TreeState,
} from '../../collections/state/types';
import {useReactAriaTreeState} from '../../collections';

export interface MultiSelectStateOptions {
  getItems: Collection['getItems'];
  disabledKeysArg?: Set<string>;
  selection: MultipleSelectionProps;
  trigger: MenuTriggerProps;
  expandable: Expandable;
}

export interface MultiSelectState
  extends MultipleSelectListState,
    MenuTriggerState {
  /** Whether the select is currently focused. */
  readonly isFocused: boolean;

  /** Sets whether the select is focused. */
  setFocused(isFocused: boolean): void;
}

/**
 * A replacement for
 * [useSelectState](https://react-spectrum.adobe.com/react-stately/useSelectState.html)
 * Provides state management for a select component. Handles building a collection
 * of items from props, handles the open state for the popup menu, and manages
 * multiple selection state.
 */
export function useMultiSelectState({
  getItems,
  selection,
  trigger,
  expandable,
}: MultiSelectStateOptions): MultiSelectState & TreeState {
  const triggerState = useMenuTriggerState(trigger || {});

  const [selectedKeys, setSelectedKeys] = useControlledState(
    selection.selectedKeys,
    selection.defaultSelectedKeys ?? new Set(),
    selection.onSelectionChange,
  );

  const state = useReactAriaTreeState({
    getItems,
    ...selection,
    ...expandable,
    disallowEmptySelection: selection.selectionMode === 'single',
    selectedKeys,
    onSelectionChange: (keys: Selection) => {
      if (!areSelectionsEqual(selectedKeys as Selection, keys)) {
        setSelectedKeys(keys);
      }

      if (selection.selectionMode === 'single') {
        triggerState.close();
      }
    },
  });

  const [isFocused, setFocused] = useState(false);

  const selectedItems = useMemo(() => {
    return [...state.selectionManager.selectedKeys]
      .map((key) => {
        return state.collection.getItem(key);
      })
      .filter((item) => item !== undefined && item !== null);
  }, [state.collection, state.selectionManager.selectedKeys]);

  return {
    ...state,
    ...triggerState,
    selectedKeys,
    setSelectedKeys,
    selectedItems,
    open() {
      if (state.collection.size !== 0) {
        triggerState.open();
      }
    },
    toggle(focusStrategy) {
      if (state.collection.size !== 0) {
        triggerState.toggle(focusStrategy);
      }
    },
    isFocused,
    setFocused,
  };
}
